import type { ReactElement } from 'react';
import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MoreVertical,
  Loader,
  User,
  MapPin,
  UserCheck,
  UserX,
  ArrowRight,
  BarChart3,
  X,
  RefreshCw,
  Users,
} from 'lucide-react';
import { representativesService, usersService } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import AssignmentModal from './modals/AssignmentModal';
import type {
  RepresentativeAssignment,
  CreateAssignmentRequest,
  UpdateAssignmentRequest,
  AssignmentStats,
  AssignmentFilters,
} from '../../types/assignments';

const RepresentativesManagement = (): ReactElement => {
  // Estados principales
  const [assignments, setAssignments] = useState<RepresentativeAssignment[]>(
    []
  );
  const [stats, setStats] = useState<AssignmentStats | null>(null);
  const [zones, setZones] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [selectedAssignmentStatus, setSelectedAssignmentStatus] = useState<
    'assigned' | 'unassigned' | 'all'
  >('all');
  const [selectedUserStatus, setSelectedUserStatus] = useState<
    'active' | 'inactive' | 'all'
  >('all');

  // Estados de modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingAssignment, setEditingAssignment] =
    useState<RepresentativeAssignment | null>(null);
  const [unassignedUsers, setUnassignedUsers] = useState<any[]>([]);

  // Estados de menú contextual - mismo patrón que UserManagement
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Toast hook
  const { toasts, success, error: errorToast, removeToast } = useToast();

  // Verificación de permisos
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? 'null') as {
        role?: string;
      } | null;
    } catch {
      return null;
    }
  }, []);

  const canManageAssignments =
    user?.role === 'SYSTEM_ADMIN' ||
    user?.role === 'ADMIN' ||
    user?.role === 'GENERAL_MANAGER';

  // Filtros activos
  const hasActiveFilters = useMemo(() => {
    return !!(
      searchTerm ||
      selectedZone ||
      selectedAssignmentStatus !== 'all' ||
      selectedUserStatus !== 'all'
    );
  }, [searchTerm, selectedZone, selectedAssignmentStatus, selectedUserStatus]);

  // Efectos para cerrar menú - mismo patrón que UserManagement
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const currentMenuRef = menuRefs.current.get(openMenuId);
        if (currentMenuRef && !currentMenuRef.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openMenuId]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openMenuId) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openMenuId]);

  // Cargar datos iniciales
  useEffect(() => {
    if (canManageAssignments) {
      loadAssignments();
      loadAuxiliaryData();
    }
  }, [canManageAssignments]);

  // Función para construir asignaciones desde datos de API existentes
  const buildAssignmentsFromApiData = async (): Promise<void> => {
    try {
      // 1. Obtener todos los usuarios con rol REPRESENTATIVE
      const usersResponse = await usersService.getAll({
        role: 'REPRESENTATIVE',
        limit: 1000,
        sortBy: 'name',
        sortOrder: 'asc',
      });

      // 2. Obtener todas las asignaciones existentes
      const representativesResponse = await representativesService.getAll({
        limit: 1000,
        sortBy: 'name',
        sortOrder: 'asc',
      });

      // 3. Mapear datos para construir RepresentativeAssignment[]
      const assignmentsData: RepresentativeAssignment[] =
        usersResponse.data.map((user) => {
          const mappedUser = {
            ...user,
            status: user.active ? 'active' : 'inactive',
          };

          const existingAssignment = representativesResponse.data.find(
            (rep) => rep.userId === user.id
          );

          const assignmentData: RepresentativeAssignment = {
            user: {
              id: mappedUser.id,
              name: mappedUser.name,
              email: mappedUser.email,
              employeeNumber: mappedUser.employeeNumber,
              role: 'REPRESENTATIVE',
              status: mappedUser.status,
              createdAt: mappedUser.createdAt,
            },
            isAssigned: !!existingAssignment,
            assignment: existingAssignment
              ? {
                  id: existingAssignment.id,
                  zoneId: existingAssignment.zoneId,
                  zone: existingAssignment.zone,
                  assignedAt:
                    existingAssignment.createdAt || new Date().toISOString(),
                }
              : undefined,
          };

          return assignmentData;
        });

      // 4. Calcular estadísticas
      const statsData: AssignmentStats = {
        totalRepresentatives: assignmentsData.length,
        assignedRepresentatives: assignmentsData.filter((a) => a.isAssigned)
          .length,
        unassignedRepresentatives: assignmentsData.filter((a) => !a.isAssigned)
          .length,
        byZone: ['NORTE', 'CENTRO', 'SUR'].map((zoneName) => ({
          zoneName: zoneName as 'NORTE' | 'CENTRO' | 'SUR',
          count: assignmentsData.filter(
            (a) => a.isAssigned && a.assignment?.zone.name === zoneName
          ).length,
        })),
      };

      setAssignments(assignmentsData);
      setStats(statsData);
    } catch (err) {
      console.error('Error building assignments:', err);
      throw err;
    }
  };

  const loadAssignments = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await buildAssignmentsFromApiData();
    } catch (err) {
      setError('Error al cargar asignaciones de representantes');
      errorToast('Error al cargar la lista de asignaciones');
      console.error('Error loading assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAuxiliaryData = async (): Promise<void> => {
    try {
      const representativesResponse = await representativesService.getAll({
        limit: 1000,
      });
      const uniqueZones = representativesResponse.data
        .map((rep) => rep.zone)
        .filter(
          (zone, index, self) =>
            index === self.findIndex((z) => z.id === zone.id)
        );

      setZones(uniqueZones);
    } catch (err) {
      console.error('Error loading auxiliary data:', err);
      const fallbackZones = [
        { id: '1', name: 'NORTE' },
        { id: '2', name: 'CENTRO' },
        { id: '3', name: 'SUR' },
      ];
      setZones(fallbackZones);
    }
  };

  const loadUnassignedUsers = async (): Promise<void> => {
    try {
      const unassigned = assignments.filter((a) => !a.isAssigned);
      setUnassignedUsers(unassigned.map((a) => a.user));
    } catch (err) {
      console.error('Error loading unassigned users:', err);
    }
  };

  // Filtrado de asignaciones - patrón de UserManagement
  const filteredAssignments = useMemo(() => {
    return assignments.filter((assignment) => {
      const matchesSearch =
        assignment.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.user.email
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        assignment.user.employeeNumber
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesZone =
        !selectedZone ||
        (assignment.isAssigned &&
          assignment.assignment?.zoneId === selectedZone);

      const matchesAssignmentStatus =
        selectedAssignmentStatus === 'all' ||
        (selectedAssignmentStatus === 'assigned' && assignment.isAssigned) ||
        (selectedAssignmentStatus === 'unassigned' && !assignment.isAssigned);

      const matchesUserStatus =
        selectedUserStatus === 'all' ||
        assignment.user.status === selectedUserStatus;

      return (
        matchesSearch &&
        matchesZone &&
        matchesAssignmentStatus &&
        matchesUserStatus
      );
    });
  }, [
    assignments,
    searchTerm,
    selectedZone,
    selectedAssignmentStatus,
    selectedUserStatus,
  ]);

  // Funciones auxiliares - mismo patrón que UserManagement
  const getZoneDisplayName = (zoneName: string): string => {
    const zoneNames = {
      NORTE: 'Norte',
      CENTRO: 'Centro',
      SUR: 'Sur',
    };
    return zoneNames[zoneName as keyof typeof zoneNames] || zoneName;
  };

  const getZoneBadgeColor = (zoneName: string): string => {
    const colors = {
      NORTE: 'bg-blue-100 text-blue-800',
      CENTRO: 'bg-green-100 text-green-800',
      SUR: 'bg-orange-100 text-orange-800',
    };
    return (
      colors[zoneName as keyof typeof colors] || 'bg-gray-100 text-gray-800'
    );
  };

  const getAssignmentStatusColor = (isAssigned: boolean): string => {
    return isAssigned
      ? 'bg-green-100 text-green-800'
      : 'bg-yellow-100 text-yellow-800';
  };

  const getUserStatusColor = (status: string): string => {
    return status === 'active'
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };
  // Handler de refresh
  const handleRefresh = (): void => {
    loadAssignments();
    loadAuxiliaryData();
  };
  // Handlers de acciones
  const handleCreateAssignment = (): void => {
    setEditingAssignment(null);
    setShowAssignModal(true);
    setOpenMenuId(null);
    loadUnassignedUsers();
  };

  const handleEditAssignment = (assignment: RepresentativeAssignment): void => {
    setEditingAssignment(assignment);
    setShowAssignModal(true);
    setOpenMenuId(null);
  };

  const handleSaveAssignment = async (
    assignmentData: CreateAssignmentRequest | UpdateAssignmentRequest
  ): Promise<void> => {
    try {
      if (editingAssignment?.isAssigned && editingAssignment.assignment) {
        // Actualizar asignación existente
        await representativesService.update(
          editingAssignment.assignment.id,
          assignmentData as UpdateAssignmentRequest
        );
        success(`Asignación actualizada correctamente`);
      } else {
        // Crear nueva asignación
        await representativesService.create(
          assignmentData as CreateAssignmentRequest
        );
        success(`Representante asignado correctamente`);
      }

      await loadAssignments();
      setShowAssignModal(false);
      setEditingAssignment(null);
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Error al guardar asignación';
      errorToast(message);
      throw err;
    }
  };

  const handleRemoveAssignment = async (
    assignment: RepresentativeAssignment
  ): Promise<void> => {
    if (!assignment.isAssigned || !assignment.assignment) {
      errorToast('No hay asignación para remover');
      return;
    }

    if (
      !confirm(
        `¿Estás seguro de que quieres quitar la asignación de "${
          assignment.user.name
        }" de la zona ${getZoneDisplayName(assignment.assignment.zone.name)}?`
      )
    ) {
      return;
    }

    try {
      await representativesService.delete(assignment.assignment.id);
      success(`Asignación removida correctamente`);
      await loadAssignments();
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Error al remover asignación';
      errorToast(message);
    }
    setOpenMenuId(null);
  };

  // Toggle menu - mismo patrón que UserManagement
  const toggleMenu = (assignmentId: string, event?: React.MouseEvent): void => {
    event?.preventDefault();
    event?.stopPropagation();
    setOpenMenuId(openMenuId === assignmentId ? null : assignmentId);
  };

  const setMenuRef = (
    assignmentId: string,
    element: HTMLDivElement | null
  ): void => {
    if (element) {
      menuRefs.current.set(assignmentId, element);
    } else {
      menuRefs.current.delete(assignmentId);
    }
  };

  // Verificación de permisos
  if (!canManageAssignments) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-xl mb-2">🚫</div>
          <div className="text-gray-600">
            No tienes permisos para gestionar asignaciones
          </div>
        </div>
      </div>
    );
  }

  // Estados de carga y error
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary-dark" />
        <span className="ml-2 text-gray-600">Cargando asignaciones...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-red-800">
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={loadAssignments}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header con estadísticas */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Asignaciones de Representantes por Zona
          </h2>
          <p className="text-sm text-gray-600">
            {filteredAssignments.length} de {assignments.length} representantes
            {stats && (
              <span className="ml-2">
                • {stats.assignedRepresentatives} asignados •{' '}
                {stats.unassignedRepresentatives} sin asignar
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleCreateAssignment}
          className="flex items-center gap-2 bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Asignar Representante
        </button>
      </div>

      {/* Panel de estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalRepresentatives}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <UserCheck className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Asignados</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.assignedRepresentatives}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <UserX className="w-8 h-8 text-yellow-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Sin Asignar</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.unassignedRepresentatives}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-600">Por Zona</p>
              {stats.byZone.map((zone) => (
                <div
                  key={zone.zoneName}
                  className="flex justify-between text-sm"
                >
                  <span>{getZoneDisplayName(zone.zoneName)}</span>
                  <span className="font-medium">{zone.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar representantes..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full"
              />
            </div>
          </div>

          {/* Zona */}
          <div className="relative w-full md:w-48">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedZone}
              onChange={(e) => {
                setSelectedZone(e.target.value);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todas las zonas</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {getZoneDisplayName(zone.name)}
                </option>
              ))}
            </select>
          </div>

          {/* Estado de Asignación */}
          <div className="relative w-full md:w-48">
            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedAssignmentStatus}
              onChange={(e) => {
                setSelectedAssignmentStatus(
                  e.target.value as 'assigned' | 'unassigned' | 'all'
                );
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="all">Todos</option>
              <option value="assigned">Asignados</option>
              <option value="unassigned">Sin asignar</option>
            </select>
          </div>

          {/* Estado de Usuario */}
          <div className="relative w-full md:w-48">
            <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedUserStatus}
              onChange={(e) => {
                setSelectedUserStatus(
                  e.target.value as 'active' | 'inactive' | 'all'
                );
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>

          {/* Botón Refresh */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Active Filters Badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-600">Filtros activos:</span>

            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Búsqueda: "{searchTerm}"
                <button
                  onClick={() => {
                    setSearchTerm('');
                  }}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedZone && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Zona:{' '}
                {getZoneDisplayName(
                  zones.find((z) => z.id === selectedZone)?.name || ''
                )}
                <button
                  onClick={() => {
                    setSelectedZone('');
                  }}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedAssignmentStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                Asignación:{' '}
                {selectedAssignmentStatus === 'assigned'
                  ? 'Asignados'
                  : 'Sin asignar'}
                <button
                  onClick={() => {
                    setSelectedAssignmentStatus('all');
                  }}
                  className="hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedUserStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                Estado:{' '}
                {selectedUserStatus === 'active' ? 'Activos' : 'Inactivos'}
                <button
                  onClick={() => {
                    setSelectedUserStatus('all');
                  }}
                  className="hover:bg-orange-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedZone('');
                setSelectedAssignmentStatus('all');
                setSelectedUserStatus('all');
              }}
              className="text-xs text-gray-600 hover:text-gray-900 underline ml-2"
            >
              Limpiar todos los filtros
            </button>
          </div>
        )}
      </div>

      {/* Tabla de asignaciones */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Representante
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Empleado #
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Estado Usuario
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Zona Asignada
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Estado Asignación
                </th>
                <th className="text-center py-3 px-4 font-medium text-gray-700">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAssignments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    <div>
                      <div className="text-xl mb-2">👥</div>
                      <div>No se encontraron asignaciones</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAssignments.map((assignment) => (
                  <tr
                    key={assignment.user.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <User className="w-8 h-8 text-gray-400 mr-3" />
                        <div>
                          <div className="text-left font-medium text-gray-900">
                            {assignment.user.name}
                          </div>
                          <div className="text-left text-sm text-gray-600">
                            {assignment.user.email ?? 'Sin email'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      <span className="font-mono text-sm">
                        {assignment.user.employeeNumber}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUserStatusColor(
                          assignment.user.status
                        )}`}
                      >
                        {assignment.user.status === 'active'
                          ? 'Activo'
                          : 'Inactivo'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {assignment.isAssigned && assignment.assignment ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getZoneBadgeColor(
                            assignment.assignment.zone.name
                          )}`}
                        >
                          <MapPin className="w-3 h-3 mr-1" />
                          {getZoneDisplayName(assignment.assignment.zone.name)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500 italic">
                          Sin asignar
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAssignmentStatusColor(
                          assignment.isAssigned
                        )}`}
                      >
                        {assignment.isAssigned ? 'Asignado' : 'Sin Asignar'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        {/* Botón de editar/asignar directo */}
                        <button
                          onClick={() => {
                            handleEditAssignment(assignment);
                          }}
                          className="p-1 text-primary-medium hover:text-primary-dark rounded transition-colors"
                          title={
                            assignment.isAssigned
                              ? 'Cambiar asignación'
                              : 'Asignar zona'
                          }
                        >
                          {assignment.isAssigned ? (
                            <Edit className="w-4 h-4" />
                          ) : (
                            <ArrowRight className="w-4 h-4" />
                          )}
                        </button>

                        {/* Menú contextual de 3 puntos */}
                        <div className="relative">
                          <button
                            onClick={(e) => toggleMenu(assignment.user.id, e)}
                            className="p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-green-950"
                            title="Más opciones"
                            aria-expanded={openMenuId === assignment.user.id}
                            aria-haspopup="true"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {openMenuId === assignment.user.id && (
                            <div
                              ref={(el) => setMenuRef(assignment.user.id, el)}
                              className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1"
                              style={{ zIndex: 1000 }}
                            >
                              {/* Editar/Asignar */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditAssignment(assignment);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                              >
                                {assignment.isAssigned ? (
                                  <>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Cambiar zona
                                  </>
                                ) : (
                                  <>
                                    <ArrowRight className="w-4 h-4 mr-2" />
                                    Asignar zona
                                  </>
                                )}
                              </button>

                              {/* Remover asignación (solo si está asignado) */}
                              {assignment.isAssigned && (
                                <>
                                  <div className="border-t border-gray-100 my-1"></div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveAssignment(assignment);
                                    }}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Quitar asignación
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de asignación */}
      <AssignmentModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setEditingAssignment(null);
        }}
        onSave={handleSaveAssignment}
        assignment={editingAssignment}
        title={
          editingAssignment?.isAssigned
            ? 'Cambiar Asignación de Zona'
            : 'Asignar Representante a Zona'
        }
        zones={zones}
        unassignedUsers={unassignedUsers}
        mode={editingAssignment?.isAssigned ? 'edit' : 'create'}
      />
    </div>
  );
};

export default RepresentativesManagement;
